import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, HelpCircle, SearchX, AlertCircle } from 'lucide-react';

import {
  Table,
  TableHeader,
  TableBody,
} from "@/components/ui/table";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "@/components/ui/alert";

import { fetchFunds } from './fundsRawQueries';
import { FundsRawTableProps, FundRawRecord, FundRawCounts, FundRawResultData } from './types';
import { ResizableFundsTable } from './ResizableFundsTable';
import { isSmallViewport } from '@/lib/utils';
import { MobileViewportWarning } from '@/components/ui/MobileViewportWarning';
import { Spinner } from '@/components/ui/Spinner';
import { getRowsPerPagePref, setRowsPerPagePref } from '@/utils/tablePreferences';

/**
 * FundsRawTable Component
 * 
 * A table component to display fund data with filtering, pagination and responsive design
 * Integrated with TanStack Table via ResizableFundsTable
 */
function FundsRawTable({ 
  filters = {},
  initialPage = 1,
  initialRowsPerPage = 25,
  queryEnabled = true,
  onCountsUpdate,
  funds: initialFunds = [],
  onAddToPortfolio = () => {},
  onFavorite = () => {},
  onAddToWatchlist = () => {},
  bulkSelection,
  onSetGetSelectedData,
}: FundsRawTableProps) {
  // State for pagination
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPagePref('funds-raw', initialRowsPerPage));
  const [pageInputValue, setPageInputValue] = useState(String(initialPage));
  
  // For responsive design - hide columns on small screens
  const [isSmallScreen, setIsSmallScreen] = useState(() => isSmallViewport(480));
  
  // Check for small viewport
  useEffect(() => {
    const checkViewport = () => setIsSmallScreen(isSmallViewport(480));
    
    // Check on window resizes
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Fetch funds data with React Query
  const { data: queryData, error, isLoading, refetch } = useQuery<FundRawResultData>({
    queryKey: ['fundsRaw', currentPage, rowsPerPage, filters?.searchText, filters?.fundType, filters?.subAssetClass, filters?.category],
    queryFn: () => fetchFunds({
      page: currentPage,
      pageSize: rowsPerPage,
      searchText: filters?.searchText,
      fundType: filters?.fundType,
      subAssetClass: filters?.subAssetClass,
      category: filters?.category
    }),
    enabled: queryEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Using placeholderData for better UX during data fetching
    placeholderData: (prev) => prev,
  });

  // Extract funds and counts from the query data with useMemo for optimization
  const funds = useMemo(() => queryData?.data || initialFunds, [queryData?.data, initialFunds]);
  const totalCount = useMemo(() => queryData?.count || 0, [queryData?.count]);
  const visibleCount = useMemo(() => funds?.length || 0, [funds]);

  // Calculate total pages
  const totalPages = useMemo(() => 
    Math.max(1, Math.ceil(totalCount / rowsPerPage)),
    [totalCount, rowsPerPage]
  );

  // Update counts effect
  useEffect(() => {
    if (onCountsUpdate) {
      onCountsUpdate({
        total: totalCount,
        filtered: totalCount, // Same as total in this implementation
        visible: visibleCount
      });
    }
  }, [totalCount, visibleCount, onCountsUpdate]);

  // Set up callback to get selected fund data
  const getSelectedFunds = useCallback(() => {
    if (!bulkSelection || !bulkSelection.isSelected || !(funds?.length || 0)) {
      return [];
    }
    return funds.filter(fund => bulkSelection.isSelected(fund.id));
  }, [bulkSelection?.isSelected, funds]);

  useEffect(() => {
    if (onSetGetSelectedData && getSelectedFunds) {
      onSetGetSelectedData(getSelectedFunds);
    }
  }, [onSetGetSelectedData, getSelectedFunds]);
  
  
  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setPageInputValue(String(page));
  }, [totalPages]);

  // Adapter functions for handler signatures
  const handleAddToPortfolio = useCallback((fund: FundRawRecord) => {
    onAddToPortfolio(fund.id, fund);
  }, [onAddToPortfolio]);

  const handleFavorite = useCallback((fund: FundRawRecord) => {
    onFavorite(fund.id);
  }, [onFavorite]);

  const handleAddToWatchlist = useCallback((fund: FundRawRecord) => {
    onAddToWatchlist(fund.id);
  }, [onAddToWatchlist]);

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback((value: string) => {
    const next = Number(value);
    setRowsPerPage(next);
    setCurrentPage(1);
    setPageInputValue('1');
    setRowsPerPagePref('funds-raw', next);
  }, []);

  // Handle page input change
  const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value);
  }, []);

  // Handle page input blur
  const handlePageInputBlur = useCallback(() => {
    const newPage = Number(pageInputValue);
    if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
      handlePageChange(newPage);
    } else {
      setPageInputValue(String(currentPage));
    }
  }, [pageInputValue, totalPages, handlePageChange, currentPage]);

  // Update input field when page changes
  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  return (
    <div className="w-full space-y-4">
      {/* Error state */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading funds</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : 'An unknown error occurred. Please try again.'}
          </AlertDescription>
        </Alert>
      )}
      
      
      {/* Table component with TanStack integration - hide on small screens */}
      {!error && !isSmallScreen && (
        <div className="rounded-md border relative">
          <AnimatePresence mode="wait">
            {isLoading ? (
              // Show skeleton placeholder while loading
              <motion.div
                key="skeleton"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full">
                <Table className="w-full">
                  <TableHeader className="bg-muted/60 sticky top-0 border-b z-10">
                    <tr>
                      <th className="w-[90px] min-w-[90px] pl-4 pr-2 py-1 text-left text-muted-foreground text-xs font-normal sticky z-10">
                        Code
                      </th>
                      <th className="w-[35%] px-4 py-1 text-left text-muted-foreground text-xs font-normal sticky z-10">
                        Fund Name
                      </th>
                      <th className={isSmallScreen ? "hidden md:table-cell" : "w-[30%] px-4 py-1 text-left text-muted-foreground text-xs font-normal sticky z-10"}>
                        Company
                      </th>
                      <th className={isSmallScreen ? "hidden md:table-cell" : "w-[15%] px-4 py-1 text-left text-muted-foreground text-xs font-normal sticky z-10"}>
                        Fund Type
                      </th>
                      <th className={isSmallScreen ? "hidden lg:table-cell" : "w-[20%] px-4 py-1 text-left text-muted-foreground text-xs font-normal sticky z-10"}>
                        Strategy
                      </th>
                      <th className="w-[60px] min-w-[60px] pl-2 pr-4 py-1 text-right text-muted-foreground text-xs font-normal sticky z-10">
                      </th>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {Array.from({ length: rowsPerPage > 10 ? 10 : rowsPerPage }).map((_, i) => (
                      <tr key={`skeleton-${i}`} className="w-full border-b border-muted">
                        <td className="w-[90px] min-w-[90px] pl-4 pr-2 py-2 align-top">
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, delay: i * 0.05 }}
                            className="h-3 w-12 bg-muted rounded"
                          />
                        </td>
                        <td className="w-[35%] px-4 py-2 align-top">
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, delay: i * 0.05 }}
                            className="h-3 w-full max-w-[200px] bg-muted rounded"
                          />
                        </td>
                        <td className={isSmallScreen ? "hidden md:table-cell" : "w-[30%] px-4 py-2 align-top"}>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, delay: i * 0.05 }}
                            className="h-3 w-full max-w-[150px] bg-muted rounded"
                          />
                        </td>
                        <td className={isSmallScreen ? "hidden md:table-cell" : "w-[15%] px-4 py-2 align-top"}>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, delay: i * 0.05 }}
                            className="h-3 w-16 bg-muted rounded"
                          />
                        </td>
                        <td className={isSmallScreen ? "hidden lg:table-cell" : "w-[20%] px-4 py-2 align-top"}>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, delay: i * 0.05 }}
                            className="h-3 w-20 bg-muted rounded"
                          />
                        </td>
                        <td className="w-[60px] min-w-[60px] pl-2 pr-4 py-2 text-right">
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, delay: i * 0.05 }}
                            className="ml-auto h-3 w-5 bg-muted rounded"
                          />
                        </td>
                      </tr>
                    ))}
                  </TableBody>
                </Table>
              </motion.div>
            ) : (
              // Render the new TanStack-powered table with data
              <motion.div
                key="table"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full">
                <ResizableFundsTable
                  data={funds}
                  isLoading={false}
                  onAddToPortfolio={handleAddToPortfolio}
                  onFavorite={handleFavorite}
                  onAddToWatchlist={handleAddToWatchlist}
                  rowsPerPage={rowsPerPage}
                  currentPage={currentPage}
                  totalCount={totalCount}
                  onPageChange={handlePageChange}
                  bulkSelection={bulkSelection}
                />
              </motion.div>
            )}
          </AnimatePresence>
          {isLoading && (
            <div className="p-4 text-center text-muted-foreground flex items-center justify-center gap-2">
              <Spinner size="sm" variant="muted" ariaLabel="Loading funds" />
              <span>Loading…</span>
            </div>
          )}
          {!isLoading && (funds?.length || 0) === 0 && (
            <div className="border rounded-md p-8 text-center w-[98%] my-4 mx-auto">
              <SearchX className="h-16 w-16 text-muted-foreground mb-4 mx-auto" />
              <h3 className="text-lg font-medium mb-2">No funds found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search criteria or filters.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Pagination controls - styled after Supabase UI - hide on mobile */}
      {!error && !isSmallScreen && ((funds?.length || 0) > 0 || isLoading) && (
        <div className="border-t p-2 flex items-center justify-between bg-muted/20">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={isLoading || currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center text-sm">
              <span className="mr-2">Page</span>
              <Input
                className="h-8 w-[60px] text-center"
                value={pageInputValue}
                onChange={handlePageInputChange}
onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const newPage = Number(pageInputValue);
                    if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
                      handlePageChange(newPage);
                    } else {
                      setPageInputValue(String(currentPage));
                    }
                  }
                }}
                onBlur={handlePageInputBlur}
                aria-label="Go to page"
              />
              <span className="mx-2">of {totalPages}</span>
            </div>
            
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={isLoading || currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            <div className="flex items-center space-x-4">
              <Select
                value={String(rowsPerPage)}
                onValueChange={handleRowsPerPageChange}
              >
                <SelectTrigger className="h-8 w-[120px]">
                  <SelectValue placeholder={`${rowsPerPage} rows`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 rows</SelectItem>
                  <SelectItem value="50">50 rows</SelectItem>
                  <SelectItem value="100">100 rows</SelectItem>
                  <SelectItem value="500">500 rows</SelectItem>
                  <SelectItem value="1000">1000 rows</SelectItem>
                </SelectContent>
              </Select>

              <div className="text-sm text-muted-foreground flex items-center">
                <span>
                  {totalCount.toLocaleString()} records 
                  {totalCount > rowsPerPage * totalPages ? " (estimated)" : ""}
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 ml-1">
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Total number of funds matching your filters</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </div>
        )}
    </div>
  );
}

export default FundsRawTable;